import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Dialog, toast } from '@utima/ui';
import { memo, useEffect, useMemo, useState } from 'react';

import { AgentAccessSelector } from '@/features/documents/components/AgentAccessSelector';
import { trpc } from '@/services/trpc';

import type { KBContentRow } from './kbTypes';
import type { useKBMutations } from '../hooks/useKBMutations';

type BulkAgentAccessModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedRows: KBContentRow[];
  mutations: ReturnType<typeof useKBMutations>;
};

type AccessStateItem = {
  chatIds: string[];
  availableForAllAgents: boolean;
};

function toAccessState(row: KBContentRow): AccessStateItem | null {
  if (row.rowType === 'document') {
    const data = row.data as Record<string, unknown>;
    const directChats = Array.isArray(data.chats)
      ? data.chats
          .map(chat => {
            if (typeof chat === 'string') return chat;
            if (typeof chat === 'object' && chat && 'id' in chat) {
              return String(chat.id);
            }

            return null;
          })
          .filter((chatId): chatId is string => chatId !== null)
      : [];

    const fallbackEffectiveChats = Array.isArray(data.effectiveChatIds)
      ? (data.effectiveChatIds.filter(
          chatId => typeof chatId === 'string',
        ) as string[])
      : [];

    return {
      chatIds: directChats.length > 0 ? directChats : fallbackEffectiveChats,
      availableForAllAgents: Boolean(data.availableForAllAgents),
    };
  }

  if (row.rowType === 'folder') {
    const data = row.data as Record<string, unknown>;
    const chats = Array.isArray(data.chats)
      ? data.chats
          .map(chat => {
            if (typeof chat === 'string') return chat;
            if (typeof chat === 'object' && chat && 'id' in chat) {
              return String(chat.id);
            }

            return null;
          })
          .filter((chatId): chatId is string => chatId !== null)
      : [];

    return {
      chatIds: chats,
      availableForAllAgents: Boolean(data.availableForAllAgents),
    };
  }

  return null;
}

export const BulkAgentAccessModal = memo(function BulkAgentAccessModal({
  open,
  onOpenChange,
  selectedRows,
  mutations,
}: BulkAgentAccessModalProps) {
  const { t } = useLingui();
  const [availableForAllAgents, setAvailableForAllAgents] = useState(false);
  const [selectedChatIds, setSelectedChatIds] = useState<string[]>([]);

  const selectedItems = useMemo(
    () =>
      selectedRows.filter(
        row => row.rowType === 'document' || row.rowType === 'folder',
      ),
    [selectedRows],
  );

  const documentIds = useMemo(
    () =>
      selectedItems
        .filter(row => row.rowType === 'document')
        .map(row => row.id),
    [selectedItems],
  );
  const folderIds = useMemo(
    () =>
      selectedItems.filter(row => row.rowType === 'folder').map(row => row.id),
    [selectedItems],
  );

  const { data: allChats } = trpc.chats.findAll.useQuery(
    {
      pagination: { pageSize: 100 },
      fields: ['id', 'name'],
    },
    { enabled: open },
  );

  const currentStateSummary = useMemo(() => {
    const items = selectedItems
      .map(toAccessState)
      .filter((item): item is AccessStateItem => item !== null);

    if (items.length === 0) {
      return {
        sharedChatIds: [] as string[],
        partialChatIds: [] as string[],
        availableForAllCount: 0,
        totalItems: 0,
      };
    }

    const chatCounts = new Map<string, number>();
    for (const item of items) {
      const uniqueChatIds = new Set(item.chatIds);
      for (const chatId of uniqueChatIds) {
        chatCounts.set(chatId, (chatCounts.get(chatId) ?? 0) + 1);
      }
    }

    const totalItems = items.length;
    const sharedChatIds = [...chatCounts.entries()]
      .filter(([, count]) => count === totalItems)
      .map(([chatId]) => chatId);
    const partialChatIds = [...chatCounts.entries()]
      .filter(([, count]) => count > 0 && count < totalItems)
      .map(([chatId]) => chatId);

    return {
      sharedChatIds,
      partialChatIds,
      availableForAllCount: items.filter(item => item.availableForAllAgents)
        .length,
      totalItems,
    };
  }, [selectedItems]);

  const chatNameById = useMemo(() => {
    return new Map((allChats?.items ?? []).map(chat => [chat.id, chat.name]));
  }, [allChats?.items]);

  const renderChatNames = (chatIds: string[]) =>
    chatIds.map(chatId => chatNameById.get(chatId) ?? chatId).join(', ');

  useEffect(() => {
    if (!open) {
      setAvailableForAllAgents(false);
      setSelectedChatIds([]);
    }
  }, [open]);

  const handleSave = async () => {
    try {
      await Promise.all([
        documentIds.length > 0
          ? mutations.bulkAssign.mutateAsync({
              documentIds,
              chatIds: availableForAllAgents ? [] : selectedChatIds,
              availableForAllAgents,
            })
          : Promise.resolve(),
        folderIds.length > 0
          ? mutations.bulkAssignFoldersToChats.mutateAsync({
              folderIds,
              chatIds: availableForAllAgents ? [] : selectedChatIds,
              availableForAllAgents,
            })
          : Promise.resolve(),
      ]);
      toast.success(t`Access settings updated`);
      onOpenChange(false);
    } catch {
      toast.error(t`Failed to update access settings`);
    }
  };

  const isSaving =
    mutations.bulkAssign.isPending ||
    mutations.bulkAssignFoldersToChats.isPending;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className='w-full max-w-[680px]'>
        <Dialog.Header>
          <Dialog.Title>
            <Trans>Agent access settings</Trans>
          </Dialog.Title>
          <Dialog.Description>
            <Trans>
              Set one agent access configuration and apply it to all selected
              documents and folders.
            </Trans>
          </Dialog.Description>
        </Dialog.Header>

        <div className='space-y-4 py-2'>
          <div className='rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-fg'>
            {currentStateSummary.totalItems === 0 ? (
              <p>
                <Trans>No eligible items selected.</Trans>
              </p>
            ) : (
              <div className='space-y-1.5'>
                {currentStateSummary.sharedChatIds.length > 0 && (
                  <p>
                    <Trans>
                      Applied to all selected items (direct or inherited):
                    </Trans>{' '}
                    {renderChatNames(currentStateSummary.sharedChatIds)}
                  </p>
                )}
                {currentStateSummary.partialChatIds.length > 0 && (
                  <p>
                    <Trans>
                      Applied to some selected items (direct or inherited):
                    </Trans>{' '}
                    {renderChatNames(currentStateSummary.partialChatIds)}
                  </p>
                )}
                {currentStateSummary.sharedChatIds.length === 0 &&
                  currentStateSummary.partialChatIds.length === 0 && (
                    <p>
                      <Trans>
                        None of the selected items currently has direct or
                        inherited agent assignments.
                      </Trans>
                    </p>
                  )}
                <p>
                  <Trans>
                    Available for all agents is currently enabled on{' '}
                    {currentStateSummary.availableForAllCount} of{' '}
                    {currentStateSummary.totalItems} selected items.
                  </Trans>
                </p>
              </div>
            )}
          </div>

          <div className='rounded-md border border-warning/40 bg-warning/10 p-3 text-sm text-warning-fg'>
            <Trans>
              Saving will overwrite agent access on all selected items. Existing
              assignments not selected here will be removed.
            </Trans>
          </div>

          <AgentAccessSelector
            availableForAllAgents={availableForAllAgents}
            setAvailableForAllAgents={setAvailableForAllAgents}
            selectedChatIds={selectedChatIds}
            setSelectedChatIds={setSelectedChatIds}
            availableAgents={allChats?.items ?? []}
          />
        </div>

        <Dialog.Footer>
          <Button variant='ghost' outline onClick={() => onOpenChange(false)}>
            <Trans>Cancel</Trans>
          </Button>
          <Button onClick={handleSave} loading={isSaving}>
            <Trans>Save</Trans>
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
});
